import Card from "components/common/Card";
import React, { useEffect, useState } from "react";
import reviewImg from "assets/icons/review.svg";
import ReviewTabs from "./component/ReviewTabs";
import ReviewDetails from "./component/ReviewDetails";
import Button from "components/semantic/Button";
import { useAppDispatch, useAppSelector } from "utils/hooks";
import { useParams } from "react-router-dom";
import { getHealthQualityThunk, postHealthQualityResultThunk } from "store/thunk/kyp.thunk";
import { useFormik } from "formik";
import ResultCountDown from "./component/ResultCountDown";

const ReviewPolicy = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [showCountDown, setShowCountDown] = useState(false);

  const dispatch = useAppDispatch();
  const param: any = useParams();
  const policyData = useAppSelector((state) => state.rootReducer.kypSlice.healthQualityData);
  const isResultLoading = useAppSelector((state) => state.rootReducer.kypSlice.healthQualityResultLoading);

  const policyTypeId = policyData?.policyId?.policyTypeId?._id === "3030b68f-82e9-4dfb-b2b8-82b743d0692a";
  const fromHospital = policyData?.policyId?.fromHospital === true;

  useEffect(() => {
    dispatch(getHealthQualityThunk(param?.policyId));
  }, [dispatch, param]);

  const { values, handleSubmit, initialValues, touched, errors, handleChange, setFieldValue, setFieldError }: any = useFormik({
    initialValues: {
      policyHolderName: policyData?.docEyeOutput?.policyHolderName,
      address: policyData?.docEyeOutput?.address,
      email: policyData?.docEyeOutput?.email,
      phone: policyData?.docEyeOutput?.phone,
      insuredDetails: policyData?.docEyeOutput?.insuredDetails,
      nomineeDetails: policyData?.docEyeOutput?.nomineeDetails
    },
    enableReinitialize: true,
    validateOnChange: false,
    onSubmit: async (values: any) => {
      const updatedValues = { ...values };

      // Compare insuredDetails
      updatedValues.insuredDetails = values.insuredDetails.map((item: any, index: number) => {
        const initial = initialValues.insuredDetails?.[index] || {};

        const cleanedItem = Object.entries(item).reduce(
          (acc, [key, value]) => {
            if (value !== "" && value !== undefined && value !== null) {
              acc[key] = value;
            }
            return acc;
          },
          {} as typeof item
        );

        return {
          ...cleanedItem,
          ...(item.name ? { name: item.name } : {}),
          ...(item.dob ? { dob: item.dob } : {}),
          ...(item.gender ? { gender: item.gender } : {}),
          ...(item.relation ? { relation: item.relation } : {}),
          ...(item.policyInceptionDate ? { policyInceptionDate: item.policyInceptionDate } : {}),
          ...(item.preExistingDiseaseName ? { preExistingDiseaseName: item.preExistingDiseaseName } : {}),
          isCorrectNameDetails: item.name === initial.name,
          isCorrectDobDetails: item.dob === initial.dob,
          isCorrectGenderDetails: item.gender === initial.gender,
          isCorrectRelationDetails: item.relation === initial.relation,
          isCorrectPolicyInceptionDateDetails: item.policyInceptionDate === initial.policyInceptionDate,
          isCorrectDiseaseDetails: item.preExistingDiseaseName === initial.preExistingDiseaseName,
          isCorrectInsuredDetails: item.name === initial.name && item.dob === initial.dob && item.gender === initial.gender && item.relation === initial.relation && item.policyInceptionDate === initial.policyInceptionDate && item.preExistingDiseaseName === initial.preExistingDiseaseName
        };
      });

      // Compare nomineeDetails
      updatedValues.nomineeDetails = values.nomineeDetails?.map((item: any, index: number) => {
        const initial = initialValues.nomineeDetails?.[index] || {};

        return {
          ...item,
          isCorrectNomineeName: item.nomineeName === initial.nomineeName,
          isCorrectNomineeAge: item.nomineeAge === initial.nomineeAge,
          isCorrectNomineeRelation: item.nomineeRelation === initial.nomineeRelation
        };
      });

      const payload = {
        userInput: updatedValues,
        advanced: true,
        policyId: param?.policyId
      };
      dispatch(postHealthQualityResultThunk(payload))
        .unwrap()
        .then((data) => {
          if (data?.success) {
            setShowCountDown(true);
          }
        });
    }
  });

  const formProps = { values, initialValues, touched, errors, handleChange, setFieldValue, setFieldError, policyTypeId, fromHospital };

  const handleNavigate = () => {
    if (activeTab === 2) handleSubmit();
    else setActiveTab(activeTab + 1);
  };

  const hasNoError = (obj: any) => {
    if (typeof obj !== "object" || obj === null) return false;

    for (const key in obj) {
      const value = obj[key];

      if (!Array.isArray(value) && key !== "") return true;

      if (Array.isArray(value)) {
        for (const item of value) {
          if (hasNoError(item)) return true;
        }
      } else if (typeof value === "object") {
        if (hasNoError(value)) return true;
      }
    }

    return false;
  };
  return (
    <>
      <h1 className="text-lg md:text-2xl font-extrabold text-gray-800 mb-4">Policyholder & Insured Preview</h1>
      <Card className="w-full" parentClassName="mb-7">
        <div className="flex items-center gap-2 py-3 px-4 border-b-[1px] w-full border-border-color">
          <img src={reviewImg} />
          {policyTypeId && fromHospital ? (
            <p className="font-medium h-full text-[13px] md:text-base">Please review your policy details and click on Next</p>
          ) : (
            <p className="font-medium h-full text-[13px] md:text-base">
              Review your policy details and click <span className="font-bold">'Edit'</span> to make corrections. We'll assist you in informing your insurance company.
            </p>
          )}
        </div>
        <ReviewTabs activeTab={activeTab} setActiveTab={setActiveTab} />

        {activeTab === 0 && <ReviewDetails id="policyHolder" formProps={formProps} />}
        {activeTab === 1 && <ReviewDetails id="insuredDetails" formProps={formProps} />}
        {activeTab === 2 && <ReviewDetails id="nomineeDetails" formProps={formProps} />}
        <div className={`hidden md:flex justify-center items-center py-4 border-t-[1px] border-border-color mt-10 `}>
          <Button title="NEXT" isLoading={isResultLoading} onClick={handleNavigate} className={`${hasNoError(errors) || (isResultLoading && "opacity-50 pointer-events-none")}`} />
        </div>
      </Card>
      <Card className={`w-full fixed bottom-0 left-0 rounded-t-[15px] bg-white flex justify-center py-5 ${isResultLoading && "opacity-50 pointer-events-none"}`} parentClassName={` shadow-none flex md:hidden `}>
        <Button title="NEXT" isLoading={isResultLoading} onClick={handleNavigate} className={`${hasNoError(errors) && "opacity-50 pointer-events-none"}`} />
      </Card>
      {showCountDown && <ResultCountDown policyId={param?.policyId} policyTypeId={policyData?.policyId?.policyTypeId?._id} />}
    </>
  );
};

export default ReviewPolicy;
